import { useEffect } from "react";
import { motion } from "framer-motion";
import { Radar, CheckCircle2, Users, Hammer, LogOut, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { Reveal, CountUp, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import FaqSection, { FAQS } from "@/components/bwg/FaqSection";
import Seo from "@/components/bwg/Seo";
import { useBooking } from "@/components/bwg/BookingModal";
import { orgSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

const RECORD = [
  { end: 24, prefix: "", suffix: "", label: "Deals closed" },
  { end: 92, prefix: "₹", suffix: " Cr", label: "Capital raised" },
  { end: 48, prefix: "", suffix: "%", label: "Average return" },
];

const GATA_STEPS = [
  { icon: Radar, title: "Infrastructure Intelligence", body: "Identified the parcel using the ROCI Engine, reading sanctioned infrastructure before the market repriced it." },
  { icon: CheckCircle2, title: "Commercial Validation", body: "Confirmed the legal path and commercial viability of the land use against the latest notifications." },
  { icon: Users, title: "Demand Assessment", body: "Validated real, registry-backed demand in the micro-market versus available supply." },
  { icon: Hammer, title: "Asset Development", body: "Structured and positioned the asset for its highest-conviction exit." },
  { icon: LogOut, title: "Premium Exit", body: "Exited at a realized 3.4× return. A confirmed historical result, not a projection." },
];

const MARKET = [
  { prefix: "₹", end: 85000, suffix: " Cr", label: "Central government allocation across infrastructure & tourism" },
  { prefix: "", end: 150000, suffix: "+", label: "Average daily footfall in Ayodhya" },
  { prefix: "", end: 100, suffix: "+", label: "Hotel keys in pipeline (Marriott, Taj, Radisson, Oberoi committed)" },
];

const PIPELINE = [
  { project: "Airport", year: "2026", value: "₹3,500 Cr", detail: "10M passengers / year capacity" },
  { project: "Railway Corridor", year: "2026", value: "₹2,800 Cr", detail: "200 km supply-chain zone" },
  { project: "Highway Phase 1", year: "2025", value: "₹4,500 Cr", detail: "Direct Delhi connectivity" },
];

export default function Ayodhya() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { open: openBooking } = useBooking();
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Ayodhya Land Investment | Bhaarat Wealth Group"
        description="Own a piece of Ayodhya's future. Where faith, infrastructure and opportunity converge, backed by ₹85,000 Cr in allocations and a new airport, rail and highway pipeline. Ayodhya real estate intelligence from BWG."
        path="/ayodhya"
        schema={[orgSchema(), faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Ayodhya", path: "/ayodhya" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.ayodhyaHero} eyebrow="Opportunity · Ayodhya, Uttar Pradesh"
        title="Own a piece of Ayodhya's future."
        sub="Where faith, infrastructure and opportunity are converging for the first time in generations." />

      {/* Track record strip */}
      <section className="bg-brand-dark py-16 border-b border-brand-offwhite/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-3 gap-6">
          {RECORD.map((r, i) => (
            <div key={i} className="text-center">
              <div className="font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-offwhite"><CountUp end={r.end} prefix={r.prefix} suffix={r.suffix} /></div>
              <p className="mt-2 text-sm text-brand-offwhite/55">{r.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gata-1184 case study */}
      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Case Study · Gata-1184</span>
              <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-brand-ink leading-[1.02]">₹11 Cr to ₹37 Cr in 5 months.</h2>
              <p className="mt-6 text-lg text-brand-grey leading-relaxed max-w-2xl">
                A parcel identified using the ROCI Engine before the market caught on, strategically positioned, diligenced end to end, and exited for a realized <span className="text-brand-ink font-medium">3.4× return</span>.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div data-testid="gata-realized-return" className="bg-brand-dark rounded-sm p-8 border border-brand-orange/40">
                <p className="text-xs tracking-[0.2em] uppercase text-brand-offwhite/50">Realized result</p>
                <div className="mt-2 font-display font-semibold text-6xl tracking-tighter text-brand-orange">3.4×</div>
                <p className="mt-2 text-brand-offwhite/70 text-sm">Gata-1184, exited. A confirmed historical result, not a forward projection.</p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-brand-ink/10 border border-brand-ink/10">
            {GATA_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="bg-brand-offwhite p-6">
                <div className="flex items-center justify-between mb-4">
                  <s.icon className="w-7 h-7 text-brand-orange" strokeWidth={1.5} />
                  <span className="font-mono text-sm text-brand-ink/20">0{i + 1}</span>
                </div>
                <h3 className="font-display font-semibold text-lg tracking-tight text-brand-ink leading-snug">{s.title}</h3>
                <p className="mt-2 text-brand-grey leading-relaxed text-sm">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Market context */}
      <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Convergence</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-offwhite max-w-3xl leading-[1.05]">A city being rebuilt around a once-in-a-generation demand event.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px mt-14 bg-brand-offwhite/10 border border-brand-offwhite/10">
            {MARKET.map((m, i) => (
              <Reveal key={i} delay={i * 0.12} className="bg-brand-dark p-10">
                <div className="font-display font-semibold text-4xl lg:text-5xl tracking-tighter text-brand-offwhite"><CountUp end={m.end} prefix={m.prefix} suffix={m.suffix} /></div>
                <p className="mt-4 text-brand-offwhite/60 leading-relaxed text-[15px] max-w-xs">{m.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure pipeline table */}
      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Infrastructure Pipeline</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] mb-12">What's being built, and when.</h2>
          </Reveal>
          <div className="border border-brand-ink/15 rounded-sm overflow-hidden">
            <div className="hidden sm:grid grid-cols-4 bg-brand-ink text-brand-offwhite text-xs tracking-[0.15em] uppercase px-6 py-4">
              <span>Project</span><span>Completion</span><span>Tender Value</span><span>Scale</span>
            </div>
            {PIPELINE.map((p, i) => (
              <Reveal key={p.project} delay={i * 0.08}>
                <div className={`grid sm:grid-cols-4 gap-2 px-6 py-6 ${i % 2 ? "bg-brand-card" : "bg-brand-offwhite"} border-t border-brand-ink/10`}>
                  <span className="font-display font-semibold text-lg text-brand-ink">{p.project}</span>
                  <span className="text-brand-grey">{p.year}</span>
                  <span className="text-brand-orange font-semibold">{p.value}</span>
                  <span className="text-brand-grey">{p.detail}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Site visit offer */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl mb-12">Come see it for yourself.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: MapPin, title: "Visit the Land", body: "Walk the parcels in person, understand the connectivity and surroundings, and see exactly what you'd be taking title to." },
              { icon: Sparkles, title: "Seek Blessings at Ram Mandir", body: "A VIP Darshan experience alongside your site visit, because Ayodhya is as much about faith as it is about foresight." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.12}>
                <div className="h-full bg-brand-dark rounded-sm p-10 relative overflow-hidden">
                  <div className="grain" />
                  <c.icon className="w-9 h-9 text-brand-orange mb-6 relative z-10" strokeWidth={1.5} />
                  <h3 className="font-display font-semibold text-2xl text-brand-offwhite relative z-10">{c.title}</h3>
                  <p className="mt-3 text-brand-offwhite/65 leading-relaxed relative z-10">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <button type="button" onClick={() => openBooking("ayodhya", "ayodhya_modal")} data-testid="ayodhya-book-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-semibold text-white hover:bg-brand-ink transition-colors">
                Arrange my Ayodhya site visit <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pull quote */}
      <section className="bg-brand-dark py-28 lg:py-36 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <Reveal>
            <blockquote className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-brand-offwhite">
              "Ayodhya has held significance for thousands of years. The infrastructure is now catching up. That gap between <span className="text-brand-orange">legacy and growth</span> is where the opportunity sits."
            </blockquote>
          </Reveal>
        </div>
      </section>

      <FaqSection />
      <Footer />
      <WhatsAppFab />
    </main>
  );
}
